import React from 'react';
import EpButton from '../components/EpButton';
import formatNumberWithAbbreviation from './numberMeasurementFormat';
import UCIUruguayasLogoImage from '../assets/images/records/record_uci-uruguayas.png';
import UTIsBrasileirasLogoImage from '../assets/images/records/record_uti-brasileiras.png';
import AnzicsLogoImage from '../assets/images/records/record_anzics.png';
import NICELogoImage from '../assets/images/records/record_nice.png';
import CritCareAsiaLogoImage from '../assets/images/records/record_crit-care-asia.png';
import MICALogoImage from '../assets/images/records/record_mica.png';
import CubreaLogoImage from '../assets/images/records/record_cubrea.png';
import SATIQLogoImage from '../assets/images/records/record_satiq.png';
import JipadLogoImage from '../assets/images/records/record_jipad.png';
import IRISLogoImage from '../assets/images/records/record_iris.png';
import AlbertaCriticalCareLogoImage from '../assets/images/records/record_alberta-critical-care.png';
import { handleToggleModal } from '../components/EpModal';
import EpTendencyChart from '../components/EpTendencyChart/EpTendencyChart';
import useTranslation from '../hooks/useTranslation';

export const convertToStringWithoutSpacesAndAccents = (content) => {
  const characters = 'aaaaaeeeeiiiioooouuuuncsyoarsnpwgnmuxzh------';
  const charactersWithAccents = 'àáäâãèéëêìíïîòóöôùúüûñçßÿœæŕśńṕẃǵǹḿǘẍźḧ·/_,:;';

  const p = new RegExp(charactersWithAccents.split('').join('|'), 'g');

  const textFormatted = content.toString().trim()
    .replace(p, c => characters.charAt(charactersWithAccents.indexOf(c)))
    .replace(/&/g, '-and-')
    .replace(/[\s\W-]+/g, '');

  return textFormatted;
};

export const generateRanges = (minRange, maxRange, minValue, maxValue) => {
  const scales = []; // Array para agrupar ranges
  const ranges = maxRange - minRange; // Quantidade de ranges
  const range = (maxValue - minValue) / ranges; // Diferença entre valor máximo e mínimo

  for (let i = 0; i < ranges; i++) {
    scales.push({
      range: i + minRange, // Index do range atual
      min: minValue + range * i,
      max: minValue + range * (i + 1),
    });
  }
  return scales;
};

// Tipos de célula das tabelas
export const emptyNumberCell = (value, valueDigits, culture) => (
  value !== undefined && value !== null && value !== '' ? <span>{formatNumberWithAbbreviation(value, valueDigits, culture || 'en-US')}</span> : <span>-</span>
);

export const emptyStringCell = ({ value }) => (
  value !== undefined && value !== null && value !== '' ? <span>{value}</span> : <span>-</span>
);

export const uniquePercentValueCell = (value, valueDigits, culture) => (
  value !== undefined && value !== null && value !== '' ? <span>{formatNumberWithAbbreviation(value, valueDigits, culture || 'en-US')}%</span> : <span>-</span>
);

export const doubleIndicatorsCell = (
  primaryValueProp, primaryValuePropDigits, isPrimaryValuePropPercentage,
  secondaryValueProp, secondaryValuePropDigits, isSecondaryValuePropPercentage,
  culture,
) => {
  let formattedPrimaryValue = '-';
  let formattedSecondaryValue = '-';

  if (primaryValueProp || primaryValueProp === 0) {
    formattedPrimaryValue = isPrimaryValuePropPercentage
      ? formatNumberWithAbbreviation(primaryValueProp, primaryValuePropDigits, culture)
      : formatNumberWithAbbreviation(primaryValueProp, primaryValuePropDigits, culture || 'en-US');
  }

  if (secondaryValueProp || secondaryValueProp === 0) {
    formattedSecondaryValue = isSecondaryValuePropPercentage
      ? formatNumberWithAbbreviation(secondaryValueProp, secondaryValuePropDigits, culture)
      : formatNumberWithAbbreviation(secondaryValueProp, secondaryValuePropDigits, culture || 'en-US');
  }

  return (
    <span>
      <strong className="table__cell--bold">
        {formattedPrimaryValue}{isPrimaryValuePropPercentage && formattedPrimaryValue !== '-' ? '%' : ''}
      </strong> | {formattedSecondaryValue}{isSecondaryValuePropPercentage && '%'}
    </span>
  );
};

export const tendencyChartCell = (cellInfo, culture) => {
  // Pega dados somente dos anos que retornem dados que não são nulos
  const yearValuesArr = cellInfo.row.original.yearValues;
  const yearValuesXaxisArr = yearValuesArr
    .filter(item => item.year !== null && item.mainValue !== null)
    .map(item => item.year);
  const yearValuesYaxisArr = yearValuesArr
    .filter(item => item.year !== null && item.mainValue !== null)
    .map(item => item.mainValue);
  const validValuesArr = yearValuesArr
    .filter(item => (item.mainValue !== null));

  // Quando a consulta retornar até somente 1 dado, o gráfico evolutivo não deve ser exibido
  if (validValuesArr.length <= 1) {
    return (<span> </span>);
  }

  return (
    <EpTendencyChart
      xaxisValues={yearValuesXaxisArr}
      yaxisValues={yearValuesYaxisArr}
      culture={culture}
      divId={yearValuesYaxisArr[0]}
    />
  );
};

export const recordsHoverableCell = (
  cellInfo, setRecordInformationsTitleFunc, setModalRecordInformationsLogoFunc,
  setModalRecordInformationsCountryNameFunc, setModalRecordInformationsHospitalsCountFunc,
  setModalRecordInformationsUnitsCountFunc, setModalRecordInformationsSourceSiteFunc,
  setModalRecordInformationsDescription,
) => {
  // Tags do Globalization
  const MoreInformation = useTranslation('More Information');

  const currentUserLanguage = localStorage.getItem('bi.userLanguage');
  let cellContent = (<span>-</span>);

  const recordsImagesArr = [
    UCIUruguayasLogoImage,
    AnzicsLogoImage,
    CritCareAsiaLogoImage,
    NICELogoImage,
    CubreaLogoImage,
    SATIQLogoImage,
    JipadLogoImage,
    IRISLogoImage,
    UTIsBrasileirasLogoImage,
    MICALogoImage,
    AlbertaCriticalCareLogoImage,
  ];

  const recordName = cellInfo ? cellInfo.row.original.regionName : null;
  let recordCode = cellInfo ? cellInfo.row.original.regionCode : null;
  const recordCountryCode = cellInfo ? cellInfo.row.original.countryCode : null;
  const recordCountryName = cellInfo ? cellInfo.row.original.countryName : null;
  const recordHospitalsCount = cellInfo
    ? formatNumberWithAbbreviation(
      cellInfo.row.original.hospitalsCount, 0, currentUserLanguage,
    ) : null;
  const recordUnitsCount = cellInfo
    ? formatNumberWithAbbreviation(
      cellInfo.row.original.unitsCount, 0, currentUserLanguage,
    ) : null;
  const recordSourceSite = cellInfo ? cellInfo.row.original.sourceSite : null;
  const recordDescription = cellInfo ? cellInfo.row.original.descriptionGroup : null;

  // Altera o recordCode de UTIs Uruguaias, UTIs Brasileiras e MICA
  // por conta da regra de negócio atual do banco
  if (recordCode === 1 && recordCountryCode === 'UY') {
    recordCode = 1;
  }

  if (recordCode === 1 && recordCountryCode === 'BR') {
    recordCode = 9;
  }

  if (recordCode === 1 && recordCountryCode === 'BE') {
    recordCode = 10;
  }

  if (recordCode === 9 && recordCountryCode === 'CA') {
    recordCode = 11;
  }

  const recordLogoIndex = recordCode - 1;

  if (recordCountryName !== undefined && recordCountryName !== null && recordCountryName !== '') {
    cellContent = (
      <>
        <span className="table__cell-record-name">{recordName}</span>

        <img
          className="table__cell-record-logo"
          src={recordsImagesArr[recordLogoIndex]}
          alt="Logo do registro"
        />

        <EpButton
          actionType="button"
          buttonText={MoreInformation}
          buttonType="link"
          customClass="table__cell-record-button"
          onClickFunc={() => {
            setRecordInformationsTitleFunc(recordName);
            setModalRecordInformationsLogoFunc(recordsImagesArr[recordLogoIndex]);
            setModalRecordInformationsCountryNameFunc(recordCountryName);
            setModalRecordInformationsHospitalsCountFunc(recordHospitalsCount);
            setModalRecordInformationsUnitsCountFunc(recordUnitsCount);
            setModalRecordInformationsSourceSiteFunc(recordSourceSite);
            setModalRecordInformationsDescription(recordDescription);
            handleToggleModal('modal--record-infos');
          }}
          // disabled={}
          buttonSize="sm"
        />
      </>
    );
  }

  return cellContent;
};

let colorsArr = [
  { background: 'rgb(254, 254, 252)', color: 'rgb(51, 51, 51)' },
  { background: 'rgb(235, 240, 245)', color: 'rgb(51, 51, 51)' },
  { background: 'rgb(203, 225, 243)', color: 'rgb(51, 51, 51)' },
  { background: 'rgb(165, 203, 236)', color: 'rgb(51, 51, 51)' },
  { background: 'rgb(104, 158, 208)', color: 'rgb(0, 0, 0)' },
  { background: 'rgb(0, 69, 138)', color: 'rgb(255, 255, 255)' },
];

if (navigator.userAgent.indexOf('MSIE') !== -1
    || navigator.appVersion.indexOf('Trident/') > 0) {
  colorsArr = [
    { background: 'rgb(254, 254, 252) !important', color: 'rgb(51, 51, 51)' },
    { background: 'rgb(235, 240, 245) !important', color: 'rgb(51, 51, 51)' },
    { background: 'rgb(203, 225, 243) !important', color: 'rgb(51, 51, 51)' },
    { background: 'rgb(165, 203, 236) !important', color: 'rgb(51, 51, 51)' },
    { background: 'rgb(104, 158, 208) !important', color: 'rgb(0, 0, 0)' },
    { background: 'rgb(0, 69, 138) !important', color: 'rgb(255, 255, 255)' },
  ];
}

// Método para definir a regra de cor das células da tabela
export const coloredConsolidatedIndicatorCell = (cellInfo, tableData) => {
  const columnId = cellInfo.column.id;

  // Monta ranges de cor a partir dos valores por coluna
  const columnValuesArr = tableData.map(item => item[columnId]);
  const minColumValue = typeof columnValuesArr[0] === 'number' ? Math.min(...columnValuesArr) : null;
  const maxColumValue = typeof columnValuesArr[0] === 'number' ? Math.max(...columnValuesArr) : null;
  const colorRanges = generateRanges(0, 6, minColumValue, maxColumValue);

  // Pega valores por linha comparando com o range da coluna respectiva
  const currentRange = colorRanges ? colorRanges.filter(range => cellInfo.value >= range.min
        && cellInfo.value <= range.max).map(option => option.range) : null;

  const currentColorsObj = currentRange ? colorsArr[currentRange] : null;

  return ({
    style: {
      color: currentColorsObj ? currentColorsObj.color : null,
      backgroundColor: currentColorsObj ? currentColorsObj.background : '#fff', // Deixa célula branca quando não tem dado
    },
  });
};

// Método para definir a regra de cor das células da tabela evolutiva
export const coloredEvolutiveIndicatorCell = (cellInfo, tableData, columnOptions) => {
  const columnHeader = cellInfo.column.Header;
  const columnHeaderAsNumber = parseInt(columnHeader, 10);

  let linesWithColumnValueArr = [];
  let columnsObjValuesArr = [];
  let columnValuesArr = [];

  const columnOptionsLength = columnOptions !== null && columnOptions !== undefined
    ? columnOptions.length : 0;

  // Descobre todos os valores por coluna
  for (let i = 0; i < columnOptionsLength; i++) {
    const columnsMatchArr = tableData
      .filter(item => (item.yearValues[i]
        ? item.yearValues[i].year === columnHeaderAsNumber : null));

    linesWithColumnValueArr = [...linesWithColumnValueArr, ...columnsMatchArr];
  }

  for (let i = 0; i < linesWithColumnValueArr.length; i++) {
    const columnsMatchObj = linesWithColumnValueArr[i].yearValues
      .find(item => (item.year === columnHeaderAsNumber));

    columnsObjValuesArr = [...columnsObjValuesArr, columnsMatchObj];
  }

  columnValuesArr = columnsObjValuesArr.map(item => (item.mainValue));

  // Define os ranges de cor
  const minColumValue = typeof columnValuesArr[0] === 'number' ? Math.min(...columnValuesArr) : null;
  const maxColumValue = typeof columnValuesArr[0] === 'number' ? Math.max(...columnValuesArr) : null;
  const colorRanges = generateRanges(0, 6, minColumValue, maxColumValue);

  // Pega valores por linha comparando com o range da coluna respectiva
  const currentRange = colorRanges ? colorRanges.filter(range => cellInfo.value >= range.min
        && cellInfo.value <= range.max).map(option => option.range) : null;

  const currentColorsObj = currentRange ? colorsArr[currentRange] : null;

  return ({
    style: {
      color: currentColorsObj ? currentColorsObj.color : null,
      backgroundColor: currentColorsObj ? currentColorsObj.background : '#fff', // Deixa célula branca quando não tem dado
    },
  });
};
